import { useMemo } from 'react';

import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';

import { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';
import { companyHub, useCompanyHub } from '@atlassian/global-shortcuts/company-hub';
import { jira, useJira } from '@atlassian/global-shortcuts/jira';
import { loom, useLoom } from '@atlassian/global-shortcuts/loom';
import { teams, useTeams } from '@atlassian/global-shortcuts/teams';

import { fg } from '@confluence/feature-gating';
import { COMPANY_HUB } from '@confluence/named-routes';
import { useSessionData } from '@confluence/session-data';

import { GlobalItemLoadables } from '../../GlobalItems/GlobalItemLoadables';

import { useLicensedProducts } from './useLicensedProducts';

// Map shortcut configs to menu identifiers
export const useShortcutItemsConfig = (): Record<string, GlobalShortcutsItem> =>
	fg('navx-932-migrate-to-hooks-confluence')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useShortcutItemsConfigNew()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useShortcutItemsConfigOld();

const useShortcutItemsConfigNew = (): Record<string, GlobalShortcutsItem> => {
	const { hasLoom, hasJira, hasTeams } = useLicensedProducts();
	const { cloudId, orgId } = useSessionData();

	const { href } = navigateToTeamsApp({
		type: 'LANDING',
		cloudId,
		orgId,
		userHasNav4Enabled: true,
	});

	const companyHubShortcutWithoutComponent = useCompanyHub(COMPANY_HUB.toUrl());
	const jiraShortcut = useJira('');
	const loomShortcut = useLoom('');
	const teamsShortcut = useTeams(href);

	return useMemo(() => {
		const companyHubShortcut = {
			...companyHubShortcutWithoutComponent,
			component: GlobalItemLoadables.companyHub,
		};

		return {
			companyHubShortcut,
			...(hasJira ? { jiraShortcut } : {}),
			...(hasLoom ? { loomShortcut } : {}),
			...(hasTeams && fg('nav4_gas_include_teams') ? { teamsShortcut } : {}),
		};
	}, [
		companyHubShortcutWithoutComponent,
		hasJira,
		jiraShortcut,
		hasLoom,
		loomShortcut,
		hasTeams,
		teamsShortcut,
	]);
};

const useShortcutItemsConfigOld = (): Record<string, GlobalShortcutsItem> => {
	const { hasLoom, hasJira, hasTeams } = useLicensedProducts();
	const { cloudId, orgId } = useSessionData();

	const { href } = navigateToTeamsApp({
		type: 'LANDING',
		cloudId,
		orgId,
		userHasNav4Enabled: true,
	});

	return useMemo(() => {
		return {
			companyHubShortcut: companyHubComponent(),
			...(hasJira ? { jiraShortcut: jira(() => '') } : {}),
			...(hasLoom ? { loomShortcut: loom(() => '') } : {}),
			...(hasTeams && fg('nav4_gas_include_teams') ? { teamsShortcut: teams(() => href) } : {}),
		};
	}, [href, hasJira, hasLoom, hasTeams]);
};

function companyHubComponent(): GlobalShortcutsItem {
	return {
		...companyHub(() => COMPANY_HUB.toUrl()),
		component: GlobalItemLoadables.companyHub,
	};
}
